import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ChatbotDialogComponent } from '../chatbot-dialog/chatbot-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { animate, state, style, transition, trigger } from '@angular/animations';


@Component({
  selector: 'kt-chatbot-table',
  templateUrl: './chatbot-table.component.html',
  styleUrls: ['./chatbot-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({height: '0px', minHeight: '0', display: 'none'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ]),
  ]
})
export class ChatbotTableComponent implements OnInit {

  @Input() dataSource: any[];  // Input data array
  @Input() sidebarUI = 'false';
  @Input() mobileView = false;
  @Input() rooty = false;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('scrollContainer') scrollContainerRef: ElementRef<HTMLElement>;
  tableDataSource = new MatTableDataSource<any>();
  totalColumns: string[];
  columnLength: number;
  stickyColumnNames: string[];
  scrollableColumnNames: string[];
  currentPageIndex: number = 0;

  expandedElement: any | null;
  nestedColumnName: string | null = null;
  nestedTableColumns: string[] = [];
  expandedExtraHeight: number = 0;

  private readonly WINDOW_SIZE = 300;   // records rendered
  private readonly ITEM_HEIGHT = 60;    // fixed height of each row
  private readonly BUFFER_ROWS = 50;    // extra rows to render above to prevent jitter
  private windowStart = 0;
  private scrollRafPending = false;
  topSpacerHeight = 0;
  bottomSpacerHeight = 0;

  constructor(
    private readonly dialog: MatDialog,
    private readonly _changeDetectorRef: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    if (!this.tableDataSource.data.length && this.dataSource && this.dataSource.length) {
      if (!this.rooty) {
        this.animateRows(this.dataSource);
      } else {
        this.windowStart = 0;
        const windowEnd = Math.min(this.WINDOW_SIZE, this.dataSource.length);
        this.tableDataSource.data = this.dataSource.slice(0, windowEnd);
        this.topSpacerHeight = 0;
        this.bottomSpacerHeight = Math.max(0, (this.dataSource.length - windowEnd) * this.ITEM_HEIGHT);
      }
      this.getDisplayedColumns(this.dataSource);
    }
  }

  ngAfterViewInit(): void {
    this.tableDataSource.paginator = this.paginator;
    if (this.rooty && this.scrollContainerRef) {
      this.scrollContainerRef.nativeElement.addEventListener('scroll', () => {
        this.onScroll(this.scrollContainerRef.nativeElement);
      });
    }
  }

  private onScroll(container: HTMLElement): void {
    if (this.scrollRafPending) return;
    this.scrollRafPending = true;
    requestAnimationFrame(() => {
      this.scrollRafPending = false;
      this.updateWindow(container.scrollTop);
    });
  }

  private updateWindow(scrollTop: number): void {
    const total = this.dataSource.length;
    let newWindowStart = 0;

    const expIndex = this.expandedElement ? this.dataSource.indexOf(this.expandedElement) : -1;
    const hasExpanded = expIndex !== -1 && this.expandedExtraHeight > 0;

    let firstVisible = 0;

    if (hasExpanded) {
      const expTop = expIndex * this.ITEM_HEIGHT;
      const expBottom = expTop + this.ITEM_HEIGHT + this.expandedExtraHeight;

      if (scrollTop <= expTop) {
        firstVisible = Math.floor(scrollTop / this.ITEM_HEIGHT);
      } else if (scrollTop <= expBottom) {
        firstVisible = expIndex;
      } else {
        firstVisible = Math.floor((scrollTop - this.expandedExtraHeight) / this.ITEM_HEIGHT);
      }
    } else {
      firstVisible = Math.floor(scrollTop / this.ITEM_HEIGHT);
    }

    newWindowStart = Math.max(0, firstVisible - this.BUFFER_ROWS);
    this.windowStart = newWindowStart;
    const windowEnd = Math.min(this.windowStart + this.WINDOW_SIZE, total);

    let topSpacer = 0;
    if (hasExpanded) {
      if (newWindowStart <= expIndex) {
        topSpacer = newWindowStart * this.ITEM_HEIGHT;
      } else {
        topSpacer = newWindowStart * this.ITEM_HEIGHT + this.expandedExtraHeight;
      }
    } else {
      topSpacer = newWindowStart * this.ITEM_HEIGHT;
    }

    let bottomSpacer = 0;
    if (hasExpanded) {
      if (windowEnd <= expIndex) {
        bottomSpacer = Math.max(0, (total - windowEnd) * this.ITEM_HEIGHT + this.expandedExtraHeight);
      } else {
        bottomSpacer = Math.max(0, (total - windowEnd) * this.ITEM_HEIGHT);
      }
    } else {
      bottomSpacer = Math.max(0, (total - windowEnd) * this.ITEM_HEIGHT);
    }

    this.tableDataSource.data = this.dataSource.slice(this.windowStart, windowEnd);
    this.topSpacerHeight = topSpacer;
    this.bottomSpacerHeight = bottomSpacer;
    this._changeDetectorRef.detectChanges();
  }

  toggleExpand(element: any): void {
    if (!this.nestedColumnName) return;

    if (this.expandedElement === element) {
      this.expandedElement = null;
      this.expandedExtraHeight = 0;
    } else {
      this.expandedElement = element;
      const nestedData = element[this.nestedColumnName];
      const numRows = nestedData && nestedData.length ? nestedData.length : 0;
      // 16px (margin) + 60px (header) + 60px * numRows
      this.expandedExtraHeight = 16 + 60 + (numRows * 60);
    }

    if (this.rooty && this.scrollContainerRef) {
      this.updateWindow(this.scrollContainerRef.nativeElement.scrollTop);
    }
  }


  animateRows(fullData: any[]): void {
    this.tableDataSource.data = [];
    let counter = 0;
    const interval = setInterval(() => {
      if (counter < fullData.length && counter < 20) {
        // Push one row at a time.
        this.tableDataSource.data.push(fullData[counter]);
        // Reassign a new array reference to trigger OnPush change detection (if used)
        this.tableDataSource.data = [...this.tableDataSource.data];
        counter++;
      } else {
        clearInterval(interval);
        // If there are more rows beyond 20, assign the full data array.
        if (fullData.length > counter) {
          this.tableDataSource.data = fullData;
        }
      }
      this._changeDetectorRef.detectChanges();
    }, 300);
  }

  getDisplayedColumns(data: any[]): void {
    if (!data || !data.length) {
      this.totalColumns = [];
      return;
    }

    let raw = Object.keys(data[0]);
    let rawValuesIndices = Object.values(data[0]).map((v, i)=> v && Array.isArray(v) ? i : -1).filter(i => i>=0);

    const nestedCols = rawValuesIndices.map(i => raw[i]);
    const nc = nestedCols.length > 0 ? nestedCols[0] : null;
    this.nestedColumnName = nc;

    if (nc) {
      const nestedArray = data.find(d => d[nc] && d[nc].length > 0)?.[nc];
      if (nestedArray && nestedArray.length > 0) {
        this.nestedTableColumns = Object.keys(nestedArray[0]);
      } else {
        this.nestedTableColumns = [];
      }
    } else {
      this.nestedTableColumns = [];
    }

    let mainCols = raw.filter((_, i) => !rawValuesIndices.includes(i));

    this.totalColumns = nc ? ['expand', ...mainCols] : mainCols;
    this.stickyColumnNames = this.rooty ? [] : mainCols.slice(0, 1);
    this.columnLength = this.totalColumns.length;
    this.scrollableColumnNames = this.rooty ? mainCols : mainCols.slice(1);
    this._changeDetectorRef.detectChanges();
  }

  openTable(): void {
    const dialogRef = this.dialog.open(ChatbotDialogComponent, {
      data: { type: 'table', data: this.dataSource }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Dialog closed with data:', result);
      }
    });
  }

  isUtcDate(value: string): boolean {
    // Check if the value matches the format 'YYYY-MM-DD HH:mm:ss UTC'
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} UTC$/.test(value);
  }

  // Convert UTC date string (format: 'YYYY-MM-DD HH:mm:ss UTC') to IST
   convertUTCToLocal(utcDate: string): string {
    const isoDate = utcDate.replace(' UTC', 'Z');
    const date = new Date(isoDate);
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }

  isUrl(value: any): boolean {
    return typeof value === 'string' && /^(https?:\/\/)/i.test(value);
  }
}
